import { lazy, type ComponentType } from "react";

const RELOADED = "hms.chunkReload";

/**
 * A page loaded when it is first opened, from a module that exports it by name.
 *
 * <p>Pages are split out so signing in does not download the charts, the report viewer and every
 * administration screen. The cost is that an open tab can outlive a deployment: the hashed chunk it
 * asks for no longer exists. The first such failure reloads the tab onto the new build; a second in
 * the same session is a real error and is thrown.</p>
 */
export function lazyPage<M extends object, K extends keyof M>(load: () => Promise<M>, name: K) {
  return lazy(async () => {
    try {
      const module = await load();
      try { sessionStorage.removeItem(RELOADED); } catch { /* storage unavailable */ }
      return { default: module[name] as ComponentType };
    } catch (error) {
      let alreadyReloaded = true;
      try {
        alreadyReloaded = sessionStorage.getItem(RELOADED) === "1";
        sessionStorage.setItem(RELOADED, "1");
      } catch { /* storage unavailable: do not risk a reload loop */ }

      if (!alreadyReloaded) {
        window.location.reload();
        return new Promise<never>(() => {});
      }
      throw error;
    }
  });
}
